import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, query, where, orderBy, documentId, getDocs, doc, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { DAY_NAMES, MONTHS, toKey, addDays, weekStart } from './dateUtils';
import { findSavedByName } from './locationUtils';

const CUSTOM = '__custom__';

// `custom` and `timesAuto` are editor-only flags; they are never saved.
const emptyStop = () => ({ location: '', address: '', startTime: '', endTime: '', custom: false, timesAuto: false });
const emptyRow = () => ({ closed: false, stops: [emptyStop()] });

// Normalize a Firestore doc to { closed, stops } (supports legacy single-location docs)
function toRow(data) {
  if (!data) return emptyRow();
  let stops;
  if (Array.isArray(data.stops)) {
    stops = data.stops.map((s) => ({
      ...emptyStop(),
      location: s.location || '',
      address: s.address || '',
      startTime: s.startTime || '',
      endTime: s.endTime || '',
    }));
  } else if (data.location) {
    stops = [{
      ...emptyStop(),
      location: data.location || '',
      address: data.address || '',
      startTime: data.startTime || '',
      endTime: data.endTime || '',
    }];
  } else {
    stops = [];
  }
  if (stops.length === 0) stops = [emptyStop()];
  return { closed: !!data.closed, stops };
}

// What actually gets published for a day (null = no entry, delete the doc)
function cleanRow(row) {
  if (row.closed) return { closed: true, stops: [] };
  const stops = row.stops
    .filter((s) => s.location.trim())
    .map((s) => ({
      location: s.location.trim(),
      address: s.address.trim(),
      startTime: s.startTime.trim(),
      endTime: s.endTime.trim(),
    }));
  return stops.length ? { closed: false, stops } : null;
}

const snapshot = (rows) => JSON.stringify(rows.map(cleanRow));

async function fetchWeek(anchor) {
  const q = query(
    collection(db, 'schedule'),
    where(documentId(), '>=', toKey(anchor)),
    where(documentId(), '<=', toKey(addDays(anchor, 6))),
    orderBy(documentId())
  );
  const snap = await getDocs(q);
  const map = {};
  snap.forEach((d) => { map[d.id] = d.data(); });
  return Array.from({ length: 7 }, (_, i) => toRow(map[toKey(addDays(anchor, i))]));
}

function ScheduleEditor({ savedLocations, onQuickSaveLocation, onDirtyChange }) {
  const [weekAnchor, setWeekAnchor] = useState(() => weekStart(new Date()));
  const [rows, setRows] = useState(null);
  const [savedSnap, setSavedSnap] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [quickMsg, setQuickMsg] = useState({});

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekAnchor, i);
    return { key: toKey(d), date: d };
  });

  const loadWeek = useCallback(async (anchor) => {
    setRows(null);
    setQuickMsg({});
    try {
      const loaded = await fetchWeek(anchor);
      setRows(loaded);
      setSavedSnap(snapshot(loaded));
    } catch (err) {
      console.error(err);
      const blank = Array.from({ length: 7 }, emptyRow);
      setRows(blank);
      setSavedSnap(snapshot(blank));
      setStatus('Could not load this week — check your connection and refresh.');
    }
  }, []);

  useEffect(() => { loadWeek(weekAnchor); }, [weekAnchor, loadWeek]);

  const dirty = !!rows && snapshot(rows) !== savedSnap;
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const confirmLeave = () =>
    !dirty || window.confirm('You have unsaved changes on this week. Leave without saving?');

  const setClosed = (i, closed) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, closed } : r)));
  };

  const updateStop = (i, si, patch) => {
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      return { ...r, stops: r.stops.map((s, sidx) => (sidx === si ? { ...s, ...patch } : s)) };
    }));
  };

  const addStop = (i) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, stops: [...r.stops, emptyStop()] } : r)));
  };

  const removeStop = (i, si) => {
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const stops = r.stops.filter((_, sidx) => sidx !== si);
      return { ...r, stops: stops.length ? stops : [emptyStop()] };
    }));
  };

  // Which UI to show for a stop: pick from the list, a chosen saved location, or free text
  const stopMode = (stop) => {
    if (stop.custom) return { mode: 'custom', match: null };
    const match = findSavedByName(savedLocations, stop.location);
    if (match) return { mode: 'saved', match };
    if (stop.location.trim() || savedLocations.length === 0) return { mode: 'custom', match: null };
    return { mode: 'pick', match: null };
  };

  const pickLocation = (i, si, value) => {
    const stop = rows[i].stops[si];
    if (value === '') {
      updateStop(i, si, {
        location: '', address: '', custom: false,
        ...(stop.timesAuto ? { startTime: '', endTime: '', timesAuto: false } : {}),
      });
      return;
    }
    if (value === CUSTOM) {
      updateStop(i, si, { custom: true });
      return;
    }
    const loc = savedLocations.find((l) => l.id === value);
    if (!loc) return;
    const hasDefaults = !!(loc.startTime || loc.endTime);
    const fillTimes = hasDefaults && (stop.timesAuto || (!stop.startTime.trim() && !stop.endTime.trim()));
    updateStop(i, si, {
      location: loc.name,
      address: loc.address,
      custom: false,
      ...(fillTimes ? { startTime: loc.startTime, endTime: loc.endTime, timesAuto: true } : {}),
    });
  };

  const quickSave = async (i, si) => {
    const s = rows[i].stops[si];
    const key = `${i}-${si}`;
    const result = await onQuickSaveLocation({
      name: s.location, address: s.address, startTime: s.startTime, endTime: s.endTime,
    });
    setQuickMsg((m) => ({ ...m, [key]: result.message }));
    if (result.ok) updateStop(i, si, { custom: false });
  };

  const shiftWeek = (delta) => {
    if (!confirmLeave()) return;
    setStatus('');
    setWeekAnchor((prev) => addDays(prev, delta * 7));
  };

  const jumpToThisWeek = () => {
    if (!confirmLeave()) return;
    setStatus('');
    setWeekAnchor(weekStart(new Date()));
  };

  const copyPreviousWeek = async () => {
    if (!window.confirm("Replace everything on this week with a copy of last week? (Nothing goes live until you tap Save Week.)")) return;
    setStatus('');
    try {
      const copied = await fetchWeek(addDays(weekAnchor, -7));
      setRows(copied);
      setStatus('Copied from last week — tap Save Week to publish.');
    } catch (err) {
      console.error(err);
      setStatus('Could not load last week — try again.');
    }
  };

  const saveWeek = async () => {
    setSaving(true);
    setStatus('');
    try {
      const batch = writeBatch(db);
      weekDays.forEach(({ key }, i) => {
        const ref = doc(db, 'schedule', key);
        const clean = cleanRow(rows[i]);
        if (clean) batch.set(ref, clean);
        else batch.delete(ref);
      });
      await batch.commit();
      setSavedSnap(snapshot(rows));
      setStatus('Saved! The schedule is live on the site.');
    } catch (err) {
      console.error(err);
      setStatus('Save failed — check your connection and try again.');
    }
    setSaving(false);
  };

  const weekEnd = addDays(weekAnchor, 6);
  const weekLabel = `${MONTHS[weekAnchor.getMonth()]} ${weekAnchor.getDate()} – ${MONTHS[weekEnd.getMonth()]} ${weekEnd.getDate()}`;
  const isCurrentWeek = toKey(weekAnchor) === toKey(weekStart(new Date()));

  return (
    <>
      <div className="admin-week-nav">
        <button className="admin-nav-btn" onClick={() => shiftWeek(-1)} aria-label="Previous week">←</button>
        <div className="admin-week-label">
          <strong>{weekLabel}</strong>
          {isCurrentWeek && <span className="admin-week-tag">This week</span>}
        </div>
        <button className="admin-nav-btn" onClick={() => shiftWeek(1)} aria-label="Next week">→</button>
      </div>
      <div className="admin-week-actions">
        {!isCurrentWeek && (
          <button className="admin-link-btn" onClick={jumpToThisWeek}>Jump to this week</button>
        )}
        <button className="admin-link-btn" onClick={copyPreviousWeek}>Copy last week</button>
      </div>

      {!rows ? (
        <div className="schedule-loading"><div className="schedule-spinner" /></div>
      ) : (
        <div className="admin-days">
          {weekDays.map(({ key, date }, i) => (
            <div key={key} className={`admin-day-card${rows[i].closed ? ' is-closed' : ''}`}>
              <div className="admin-day-head">
                <strong>{DAY_NAMES[i]} · {MONTHS[date.getMonth()]} {date.getDate()}</strong>
                <label className="admin-closed-toggle">
                  <input
                    type="checkbox"
                    checked={rows[i].closed}
                    onChange={(e) => setClosed(i, e.target.checked)}
                  />
                  Closed
                </label>
              </div>
              {!rows[i].closed && (
                <>
                  {rows[i].stops.map((stop, si) => {
                    const { mode, match } = stopMode(stop);
                    const selectValue = mode === 'saved' ? match.id : mode === 'custom' ? CUSTOM : '';
                    const msgKey = `${i}-${si}`;
                    return (
                      <div key={si} className={`admin-stop${si > 0 ? ' admin-stop-divider' : ''}`}>
                        {rows[i].stops.length > 1 && (
                          <div className="admin-stop-head">
                            <span className="admin-stop-label">Stop {si + 1}</span>
                            <button
                              type="button"
                              className="admin-remove-btn"
                              onClick={() => removeStop(i, si)}
                              aria-label={`Remove stop ${si + 1}`}
                            >
                              ✕ Remove
                            </button>
                          </div>
                        )}
                        <div className="admin-day-fields">
                          <select
                            value={selectValue}
                            onChange={(e) => pickLocation(i, si, e.target.value)}
                            aria-label="Location"
                          >
                            <option value="">Choose a location…{savedLocations.length === 0 ? ' (none saved yet)' : ''}</option>
                            {savedLocations.map((l) => (
                              <option key={l.id} value={l.id}>{l.name}</option>
                            ))}
                            <option value={CUSTOM}>✎ Type a different location</option>
                          </select>
                          {mode === 'saved' && (stop.address || match.address) && (
                            <p className="admin-saved-addr">{stop.address || match.address}</p>
                          )}
                          {mode === 'custom' && (
                            <>
                              <input
                                type="text"
                                placeholder="Location name (leave blank = TBA)"
                                value={stop.location}
                                onChange={(e) => updateStop(i, si, { location: e.target.value })}
                              />
                              <input
                                type="text"
                                placeholder="Address"
                                value={stop.address}
                                onChange={(e) => updateStop(i, si, { address: e.target.value })}
                              />
                              {stop.location.trim() && (
                                <div className="admin-inline-actions">
                                  <button
                                    type="button"
                                    className="admin-link-btn admin-link-sm"
                                    onClick={() => quickSave(i, si)}
                                  >
                                    + Save to my locations
                                  </button>
                                  {quickMsg[msgKey] && <span className="admin-meta">{quickMsg[msgKey]}</span>}
                                </div>
                              )}
                            </>
                          )}
                          <div className="admin-time-row">
                            <input
                              type="text"
                              placeholder="Start (8:00 AM)"
                              value={stop.startTime}
                              onChange={(e) => updateStop(i, si, { startTime: e.target.value, timesAuto: false })}
                            />
                            <input
                              type="text"
                              placeholder="End (2:00 PM)"
                              value={stop.endTime}
                              onChange={(e) => updateStop(i, si, { endTime: e.target.value, timesAuto: false })}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <button type="button" className="admin-add-stop-btn" onClick={() => addStop(i)}>
                    + Add another location
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <button className="cta-button admin-full-btn" onClick={saveWeek} disabled={saving || !rows || !dirty}>
        {saving ? 'Saving...' : dirty ? 'Save Week' : 'Saved'}
      </button>
      {dirty && <p className="admin-unsaved">Unsaved changes — tap Save Week to publish</p>}
      {status && <p className="admin-status">{status}</p>}
    </>
  );
}

export default ScheduleEditor;
